package com.evoapps.evoshift.ui.user

import com.evoapps.evoshift.util.usesDecimalComma
import com.evoapps.evoshift.domain.model.SystemContractType
import com.evoapps.evoshift.domain.model.TeamLeaveBalance
import com.evoapps.evoshift.ui.finance.HoursExport
import com.evoapps.evoshift.ui.reports.ExportFile

/** The column titles and contract names of [LeaveBalancesExport], in the app's language. */
data class LeaveExportLabels(
    val language: String,
    val year: String,
    val employee: String,
    val contract: String,
    val entitlement: String,
    val carryOver: String,
    val total: String,
    val used: String,
    val pending: String,
    val remaining: String,
    val onDemandUsed: String,
    val onDemandLimit: String,
    val contractName: (SystemContractType) -> String
)

/**
 * SH-113: everyone's annual leave in a year as a CSV, one row per person, in working days: the
 * entitlement in force (empty when nobody knows it), the carry-over, their total, taken, waiting
 * for a decision, left, and the days on demand taken and allowed. The same file conventions as
 * [HoursExport]: ";" for Polish, "," for English, UTF-8 with a BOM, CRLF, formulas defused.
 */
object LeaveBalancesExport {
    fun fileName(workplaceId: String, year: Int) = "evoshift-leave-${workplaceId}_$year.csv"

    fun file(workplaceId: String, year: Int, rows: List<TeamLeaveBalance>, labels: LeaveExportLabels): ExportFile =
        ExportFile(fileName(workplaceId, year), HoursExport.MIME_TYPE, csv(year, rows, labels))

    fun csv(year: Int, rows: List<TeamLeaveBalance>, labels: LeaveExportLabels): String {
        val separator = if (usesDecimalComma(labels.language)) ";" else ","
        val header = listOf(
            labels.year, labels.employee, labels.contract, labels.entitlement, labels.carryOver, labels.total,
            labels.used, labels.pending, labels.remaining, labels.onDemandUsed, labels.onDemandLimit
        )
        val lines = rows.map { row ->
            val b = row.balance
            listOf(
                year.toString(),
                row.displayName,
                row.contract?.let(labels.contractName).orEmpty(),
                if (b.isKnown) b.entitlementDays.toString() else "",
                b.carryOverDays.toString(),
                b.totalDays.toString(),
                b.usedDays.toString(),
                b.pendingDays.toString(),
                b.remainingDays.toString(),
                b.onDemandUsedDays.toString(),
                b.onDemandLimit.toString()
            )
        }
        return BOM + (listOf(header) + lines).joinToString("") { row ->
            row.joinToString(separator) { HoursExport.cell(it, separator) } + "\r\n"
        }
    }

    private const val BOM = "﻿"
}
